import { useState, useEffect } from 'react';

export interface AppSettings {
  isMetric: boolean;
  use24h: boolean;
  dateFormat: string; // raw preference string: 'MM/DD/YYYY' | 'DD/MM/YYYY' | etc.
}

const DEFAULTS: AppSettings = {
  isMetric: true,
  use24h: false,
  dateFormat: 'MM/DD/YYYY',
};

function loadSettings(): AppSettings {
  try {
    const saved = localStorage.getItem('koda_settings');
    if (!saved) return { ...DEFAULTS };
    const s = JSON.parse(saved);
    return {
      isMetric: s.metric ?? true,
      use24h: s.timeFormat === '24h',
      dateFormat: s.dateFormat ?? 'MM/DD/YYYY',
    };
  } catch {
    return { ...DEFAULTS };
  }
}

/**
 * Reads all user preferences from localStorage and reacts to changes
 * made in other tabs/contexts (e.g. the Options page).
 *
 * Returns: { isMetric, use24h, dateFormat }
 */
export function useSettings(): AppSettings {
  const [settings, setSettings] = useState<AppSettings>(() => loadSettings());

  useEffect(() => {
    const handler = () => setSettings(loadSettings());
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  return settings;
}
